import { useEffect, useState, useCallback } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../api';

const NV = { bg: '#0c0a1a', card: '#1a1035', border: '#2d1b69', accent: '#a78bfa', muted: '#7c6aad', hover: '#221548' };
const inputCls = "w-full rounded-lg px-4 py-2.5 text-sm text-white outline-none focus:ring-2 focus:ring-purple-500/50";
const inputStyle = { backgroundColor: '#0c0a1a', border: `1px solid ${NV.border}` };

const DIFF_MAP = {
  facile: { emoji: '🟢', color: '#4ade80', bg: '#14532d', label: 'Facile' },
  media: { emoji: '🟡', color: '#fbbf24', bg: '#3b2f0a', label: 'Media' },
  difficile: { emoji: '🔴', color: '#f87171', bg: '#3b1c1c', label: 'Difficile' },
};

function Toggle({ value, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
      style={{ backgroundColor: value ? '#7c3aed' : '#2d1b69' }}>
      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

function SortableItem({ item, fornitori, onToggle, onDelete, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const diff = DIFF_MAP[item.difficolta_vendita] || DIFF_MAP.media;
  const forn = fornitori.find((f) => f.id === item.fornitore_id);

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 px-4 py-3 transition-colors"
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = NV.hover}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-lg select-none" style={{ color: NV.muted }} title="Trascina per riordinare">
        ⠿
      </button>
      {item.foto_url ? (
        <img src={item.foto_url} alt="" className="w-[60px] h-[60px] rounded-lg object-cover flex-shrink-0" style={{ border: `1px solid ${NV.border}` }} />
      ) : (
        <div className="w-[60px] h-[60px] rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: NV.border }}>
          <span style={{ color: NV.muted }}>📷</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white font-semibold text-sm">{item.titolo}</span>
          {forn && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#2d1b69', color: NV.accent }}>{forn.nome}</span>}
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: diff.bg, color: diff.color }}>{diff.emoji} {diff.label}</span>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs">
          <span className="font-bold" style={{ color: '#4ade80' }}>€{item.prezzo_vendita}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {forn?.link_whatsapp && (
          <a href={forn.link_whatsapp} target="_blank" rel="noopener noreferrer"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-opacity hover:opacity-80" style={{ backgroundColor: '#16a34a' }} title="WhatsApp fornitore">
            💬
          </a>
        )}
        <button onClick={() => onEdit(item)} className="w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-opacity hover:opacity-80" style={{ backgroundColor: '#1e3a5f' }} title="Modifica">
          ✏️
        </button>
        <Toggle value={item.attivo} onChange={(v) => onToggle(item.id, v)} />
        <button onClick={() => onDelete(item.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-opacity hover:opacity-80" style={{ backgroundColor: '#991b1b' }} title="Elimina">
          🗑️
        </button>
      </div>
    </div>
  );
}

export default function BestProductManager() {
  const [items, setItems] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ titolo: '', fornitore_id: '', prezzo_vendita: '', difficolta_vendita: 'media', foto_url: '', attivo: true });
  const [editingId, setEditingId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const loadAll = useCallback(() => {
    Promise.all([
      api.get('/admin/best-product').then((r) => setItems(r.data)),
      api.get('/admin/best-product-fornitori').then((r) => setFornitori(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const resetForm = () => { setForm({ titolo: '', fornitore_id: '', prezzo_vendita: '', difficolta_vendita: 'media', foto_url: '', attivo: true }); setEditingId(null); };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      titolo: item.titolo || '',
      fornitore_id: item.fornitore_id ? String(item.fornitore_id) : '',
      prezzo_vendita: item.prezzo_vendita || '',
      difficolta_vendita: item.difficolta_vendita || 'media',
      foto_url: item.foto_url || '',
      attivo: item.attivo !== false,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    if (!form.titolo.trim()) return alert('Inserisci un nome prodotto');
    const payload = {
      titolo: form.titolo, descrizione: '', prezzo_acquisto: 0,
      prezzo_vendita: parseFloat(form.prezzo_vendita) || 0,
      foto_url: form.foto_url, difficolta_vendita: form.difficolta_vendita,
      fornitore_id: form.fornitore_id ? parseInt(form.fornitore_id) : null,
      attivo: form.attivo,
    };
    try {
      if (editingId) {
        await api.put(`/admin/best-product/${editingId}`, payload);
      } else {
        await api.post('/admin/best-product', payload);
      }
      resetForm(); loadAll();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const handleToggle = async (id, attivo) => {
    try {
      await api.put(`/admin/best-product/${id}`, { attivo });
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, attivo } : i));
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    await api.delete(`/admin/best-product/${id}`);
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const newItems = arrayMove(items, oldIdx, newIdx);
    setItems(newItems);
    try {
      await api.post('/admin/best-product/reorder', { order: newItems.map((i) => i.id) });
    } catch {}
  };

  const selectedForn = fornitori.find((f) => f.id === parseInt(form.fornitore_id));

  return (
    <>
      {/* Form */}
      <div className="rounded-2xl border p-5 mb-6" style={{ backgroundColor: NV.card, borderColor: NV.border }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold" style={{ color: NV.accent }}>{editingId ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</h3>
          {editingId && (
            <button onClick={resetForm} className="text-xs font-semibold px-3 py-1 rounded-full transition-opacity hover:opacity-80" style={{ background: '#2d1b69', color: NV.muted }}>
              Annulla modifica
            </button>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-xs font-semibold mb-1.5" style={{ color: NV.muted }}>Nome Prodotto</label>
          <input className={inputCls} style={inputStyle} value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="Es. Nike Air Max 90" />
        </div>

        <div className="mb-4">
          <label className="block text-xs font-semibold mb-1.5" style={{ color: NV.muted }}>Fornitore</label>
          <select className={inputCls} style={inputStyle} value={form.fornitore_id} onChange={(e) => setForm({ ...form, fornitore_id: e.target.value })}>
            <option value="">-- Nessun fornitore --</option>
            {fornitori.map((f) => <option key={f.id} value={f.id}>{f.nome} ({f.categoria})</option>)}
          </select>
          {selectedForn?.link_whatsapp && (
            <a href={selectedForn.link_whatsapp} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-lg text-xs font-bold transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#16a34a', color: 'white' }}>
              💬 Contatta su WhatsApp
            </a>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: NV.muted }}>Prezzo di Resell €</label>
            <input type="number" step="0.01" className={inputCls} style={inputStyle} value={form.prezzo_vendita} onChange={(e) => setForm({ ...form, prezzo_vendita: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: NV.muted }}>Difficolta di vendita</label>
            <div className="flex gap-2">
              {Object.entries(DIFF_MAP).map(([key, d]) => (
                <button key={key} onClick={() => setForm({ ...form, difficolta_vendita: key })}
                  className="flex-1 py-2 rounded-lg text-xs font-bold transition-all"
                  style={{
                    backgroundColor: form.difficolta_vendita === key ? d.color : '#0c0a1a',
                    color: form.difficolta_vendita === key ? '#0c0a1a' : d.color,
                    border: `2px solid ${form.difficolta_vendita === key ? d.color : NV.border}`,
                    opacity: form.difficolta_vendita === key ? 1 : 0.6,
                  }}>
                  {d.emoji} {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-semibold mb-1.5" style={{ color: NV.muted }}>URL Foto</label>
          <input className={inputCls} style={inputStyle} value={form.foto_url} onChange={(e) => setForm({ ...form, foto_url: e.target.value })} placeholder="https://..." />
          {form.foto_url && (
            <div className="mt-2">
              <img src={form.foto_url} alt="Preview" className="w-24 h-24 rounded-lg object-cover" style={{ border: `1px solid ${NV.border}` }}
                onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold" style={{ color: NV.muted }}>Attivo</span>
            <Toggle value={form.attivo} onChange={(v) => setForm({ ...form, attivo: v })} />
          </div>
          <button onClick={handleSave} className="text-xs font-bold px-6 py-2.5 rounded-lg transition-opacity hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)', color: 'white' }}>
            {editingId ? 'Aggiorna' : 'Salva'}
          </button>
        </div>
      </div>

      {/* Lista con drag and drop */}
      <div className="rounded-2xl border overflow-hidden" style={{ backgroundColor: NV.card, borderColor: NV.border }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: NV.border }}>
          <h2 className="text-lg font-bold" style={{ color: NV.accent }}>Best Products ({items.length})</h2>
          <span className="text-[10px]" style={{ color: NV.muted }}>Trascina ⠿ per riordinare</span>
        </div>
        {loading ? <div className="p-8 text-center" style={{ color: NV.muted }}>Caricamento...</div> : items.length === 0 ? <div className="p-8 text-center" style={{ color: NV.muted }}>Nessun prodotto</div> : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y" style={{ borderColor: '#1e1245' }}>
                {items.map((item) => (
                  <SortableItem key={item.id} item={item} fornitori={fornitori} onToggle={handleToggle} onDelete={handleDelete} onEdit={handleEdit} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </>
  );
}
